import React, { useState } from 'react';
import { useLogging } from '../contexts/LoggingContext';
import './UploadLogs.css';

export const UploadLogs: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { logs, getLogs, clearLogs, getLogCount, addLog } = useLogging();
    const [lastUpdated, setLastUpdated] = useState<string>('');

    const refreshLogs = () => {
        // Force a re-render by updating timestamp
        setLastUpdated(new Date().toISOString());
        console.log('🔗 UploadLogs: Manual refresh triggered at', lastUpdated);
    };

    const copyLogsToClipboard = () => {
        if (logs.length === 0) {
            alert('No logs to copy!');
            return;
        }
        
        const logsToCopy = logs.map(log => `[${log.timestamp}] ${log.message}${log.data ? ': ' + JSON.stringify(log.data, null, 2) : ''}`).join('\n');
        
        navigator.clipboard.writeText(logsToCopy).then(() => {
            // Show success feedback
            const copyBtn = document.querySelector('.log-btn-copy') as HTMLButtonElement;
            if (copyBtn) {
                const originalText = copyBtn.textContent;
                copyBtn.textContent = '✅ Copied!';
                copyBtn.style.backgroundColor = '#4caf50';
                setTimeout(() => {
                    copyBtn.textContent = originalText;
                    copyBtn.style.backgroundColor = 'white';
                }, 2000);
            }
        }).catch(err => {
            console.error('Failed to copy logs to clipboard:', err);
            // Fallback for older browsers or when clipboard API fails
            const textArea = document.createElement('textarea');
            textArea.value = logsToCopy;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                alert('Logs copied to clipboard!');
            } catch (fallbackErr) {
                console.error('Fallback copy failed:', fallbackErr);
                alert('Failed to copy logs. Please select and copy manually.');
            }
            document.body.removeChild(textArea);
        });
    };

    return (
        <div className="upload-logs-container">
            <button
                onClick={() => {
                    if (!isOpen) {
                        refreshLogs();
                    }
                    setIsOpen(!isOpen);
                }}
                className="upload-logs-toggle"
                title="View Upload Logs"
            >
                📝 Logs ({getLogCount()})
            </button>
            
            {isOpen && (
                <div className="upload-logs-panel">
                    <div className="upload-logs-header">
                        <div>
                            <h3>Upload Logs</h3>
                            {lastUpdated && (
                                <small style={{ color: '#666', fontSize: '12px' }}>
                                    Last updated: {new Date(lastUpdated).toLocaleTimeString()}
                                </small>
                            )}
                        </div>
                        <div className="upload-logs-actions">
                            <button 
                                className="refresh-btn" 
                                onClick={refreshLogs}
                                title="Refresh Logs"
                            >
                                🔄
                            </button>
                            <div className="log-actions">
                              <button onClick={clearLogs} className="log-btn log-btn-clear">
                                🗑️ Clear Logs ({getLogCount()})
                              </button>
                              <button onClick={copyLogsToClipboard} className="log-btn log-btn-copy">
                                📋 Copy Logs
                              </button>
                            </div>
                            <button onClick={() => setIsOpen(false)} className="close-btn">
                                ✕ Close
                            </button>
                        </div>
                    </div>
                    
                    <div className="upload-logs-content">
                        {/* Debug Info */}
                        <div className="debug-info">
                            <strong>Debug Info:</strong><br/>
                            Logs in context: {logs.length}<br/>
                            Logs in localStorage: {(() => {
                                try {
                                    const saved = localStorage.getItem('auditio-upload-logs');
                                    return saved ? JSON.parse(saved).length : 0;
                                } catch { return 'error'; }
                            })()}<br/>
                            Last updated: {lastUpdated || 'never'}
                        </div>
                        
                        {logs.length === 0 ? (
                            <p>No upload logs yet. Try uploading a file!</p>
                        ) : (
                            <div className="logs-list">
                                {logs.map((log) => (
                                    <div key={log.id} className="log-entry">
                                        <pre>{`[${log.timestamp}] ${log.message}${log.data ? ': ' + JSON.stringify(log.data, null, 2) : ''}`}</pre>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}; 